import loadADIInvoices, {
    DefaultDate,
    invoiceParams,
    leadingZero,
} from "@NA/Handlers/InvoiceHistory/ADIInvoiceHistoryHandler";
import MailIcon from "@NA/Icons/MailIcon";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    invoiceResults: state.ADIGlobalData.ADIInvoiceStore.invoiceResults,
    allLocationsInvoice: state.ADIGlobalData.ADIInvoiceStore.allLocationsInvoice,
    getInvoiceParameters: state.ADIGlobalData.ADIInvoiceStore.loadInvoiceParameters,
});

const mapDispatchToProps = {
    loadADIInvoices,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceCustomerInfoStyles {
    container?: InjectableCss;
    info?: TypographyProps;
    content?: TypographyProps;
    emailIcon?: LinkProps;
    title?: TypographyProps;
    filtersContainer?: GridContainerProps;
    poNumberGrid?: GridItemProps;
    clearBtnGridItem?: GridItemProps;
    clearBtn?: ButtonProps;
    input?: TextFieldPresentationProps;
    invoiceContainer?: InjectableCss;
}

export const tableStyles: InvoiceCustomerInfoStyles = {
    container: {
        css: css`
            display: flex;
            margin-top: 32px;
            margin-bottom: 1.5rem;
            justify-content: flex-end;
            align-items: center;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: block;
                        border-bottom: 1px solid #ddd;
                        padding-bottom: 12px;
                        margin-top: 0px;
                    `,
                    css`
                        display: block;
                        margin-top: 0px;
                    `,
                    css`
                        margin-top: 1rem;
                        justify-content: center;
                    `,
                    css`
                        margin-top: 1rem;
                        justify-content: center;
                    `,
                    null,
                ]);
            }}
            @media (min-width: 900px) and (max-width: 1200px) {
                    white-space: nowrap;
                    margin: 24px 0px;
                    min-height: 38px;
                    //justify-content: flex-end;
            }
        `,
    },
    info: {
        as: "span",
        css: css`
            font-size: ${getProp("theme.typography.headerTertiary.size")};

            &.title {
                font-weight: bold;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            display: block;
                        `,
                        css`
                            display: block;
                        `,
                        null,
                        null,
                        null,
                    ]);
                }}
            }
        `,
    },
    content: {
        as: "span",
        css: css`
            padding-right: 7px;
            padding-left: 7px;
            margin-right: 7px;
            border-right: 1px solid ${ADIColors.gray3};
            text-transform: capitalize;
            display: inline-block;
            line-height: 12px;
            font-weight: normal;
            color: ${getColor("common.backgroundContrast")};
        `,
    },
    emailIcon: {
        css: css`
            padding-left: 10px;
            line-height: 12px;
            transform: scale(0.9);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    title: {
        as: "h2",
        css: css`
            font-size: 18px;
            color: ${ADIColors.darkBlue1};
            padding-bottom: 16px;
            margin-top: 12px;
            font-weight: bold;
            line-height: 1.18;
        `,
    },
    filtersContainer: {
        css: css`
            ${GridOffset} {
                border-bottom: 1px solid ${getColor("common.border")};
                > ${GridItemStyle} {
                    padding: 15px 8px 15px 15px;
                    ${({ theme }) => { return breakpointMediaQueries(
                        theme, [css` display: none; `,],"max",); 
                    }}
                }
            }
        `,
    },
    poNumberGrid: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    clearBtnGridItem: {
        css: css`
            flex-direction: column;
            flex-basis: 100%;
            max-width: 100%;
            align-items: flex-end;
            .react-datetime-picker {
                height: 40px !important;
                transform: translateY(-2px);

                :focus,
                :focus-within {
                    svg {
                        transform: translate(10px, 0px);
                        outline: 0 !important;
                    }
                }
            }
        `,
    },
    invoiceContainer: {
        css: css`
            width: 100%;
        `,
    },
    clearBtn: {
        css: css`
            width: auto;
            border-radius: 4px;
            background-color: ${ADIColors.white};
            border-color: ${getColor("primary")};
            span {
                color: ${getColor("primary")};
            }

            :hover {
                background-color: ${ADIColors.white};
                border-color: ${getColor("primary")};
                span {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            css: css`
                padding: 6px;
                transform: rotate(90deg);
                svg {
                    height: 20px;
                    width: 20px;
                    color: ${getColor("common.background")};
                    top: -5px;
                    position: relative;
                }
            `,
        },
        border: "rounded",
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                border-radius: 3px;
                font-size: 12px;
                width: 100%;
                float: right;
                height: 34px;
                padding: 0 40px 0 10px;
                font-weight: 600;
                :focus-within {
                    padding: 0 40px 0 10px;
                }
            `,
            formInputWrapper: css`
                display: flex;
                > button {
                    width: 32px !important;
                    height: 34px !important;
                    background: ${ADIColors.blue5};
                    border-radius: 0 4px 4px 0;
                    > ${IconWrapper} {
                        top: 0px;
                        right: 0px;
                        width: 40px;
                        height: 35px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            max-width: 350px;
                        `,
                    ])}
            `,
        },
    },
};

const styles = tableStyles;

const InvoiceCustomerInfo = ({ invoiceResults, allLocationsInvoice, getInvoiceParameters, loadADIInvoices }: Props) => {
    const [poNumber, setPoNumber] = React.useState("");
    const [selectedDateRange, setSeletecDateRange] = React.useState("Last 180 days");
    const [fromDate, setFromDate] = React.useState(getInvoiceParameters.fromDate);
    const [toDate, setToDate] = React.useState(getInvoiceParameters.toDate);
    const customerInfo = invoiceResults?.properties;
    const [isMobile, setIsMobile] = React.useState(false);
    React.useEffect(() => {
        const onWindowResize = () => {
            setIsMobile(window.innerWidth < 700);
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);
    if (!invoiceResults?.properties || allLocationsInvoice) {
        return null;
    }

    const formatNumber = (number: string) => {
        var numericOnly = number.replace(/\D/g, "");

        var formattedNumber =
            "(" + numericOnly.substring(0, 3) + ") " + numericOnly.substring(3, 6) + "-" + numericOnly.substring(6, 10);

        return formattedNumber;
    };

    const changeFormat = (Date2: string) => {
        let date1 = new Date(Date2);
        return leadingZero(date1.getMonth() + 1) + "/" + leadingZero(date1.getDate()) + "/" + date1.getFullYear();
    };
    const handleSearch = () => {
        if (getInvoiceParameters) {
            let selectedFromDate;
            let selectedToDate;
            if (selectedDateRange !== "Custom") {
                selectedFromDate = changeFormat(
                    DefaultDate(true, false, Number(selectedDateRange.split(" ").splice(1)[0])) || "",
                );
                selectedToDate = changeFormat(
                    DefaultDate(false, false, Number(selectedDateRange.split(" ").splice(1)[0])) || "",
                );
            } else {
                selectedFromDate = changeFormat(fromDate || "");
                selectedToDate = changeFormat(toDate || "");
            }

            loadADIInvoices({
                ...getInvoiceParameters,
                invoiceNumber: poNumber ? poNumber : "",
                dpdFilterType: "dpd",
                dpdFilterData: "0",
                selectedpage: 1,
                dateRange: selectedDateRange.split(" ").splice(1).join(""),
                dateRangeDesc: selectedDateRange,
                fromDate: selectedFromDate,
                toDate: selectedToDate,
            });
        }
    };

    const clearSearch = () => {
        setPoNumber("");
        setSeletecDateRange("Last 180 days");
        if (getInvoiceParameters) {
            loadADIInvoices({
                ...invoiceParams,
                customerSequence: getInvoiceParameters.customerSequence,
                dateRange: getInvoiceParameters.dateRange === "180days" ? "180" : "1",
            });
        }
    };
    return (
        <>
            {!isMobile ? (
                <StyledWrapper {...styles.container}>
                    {customerInfo?.creditRep && (
                        <Typography {...styles.info} className="title">
                            {translate("Credit Rep:")}
                            <Typography {...styles.content}>{customerInfo.creditRep.toLowerCase()}</Typography>
                        </Typography>
                    )}
                    {customerInfo?.phoneNumber && (
                        <Typography {...styles.info}>
                            {translate("Phone:")}
                            <Typography {...styles.content}>{formatNumber(customerInfo.phoneNumber)}</Typography>
                        </Typography>
                    )}
                    {customerInfo?.faxNumber && (
                        <Typography {...styles.info}>
                            {translate("Fax:")}
                            <Typography {...styles.content}>{formatNumber(customerInfo.faxNumber)}</Typography>
                        </Typography>
                    )}
                    {customerInfo?.email && (
                        <Link {...styles.emailIcon} href={`mailto:${customerInfo.email}`}>
                            <MailIcon />
                        </Link>
                    )}
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper {...styles.container}>
                        {customerInfo?.creditRep && (
                            <Typography {...styles.info} className="title">
                                {translate("Credit Rep:")}
                                <Typography {...styles.content}>{customerInfo.creditRep.toLowerCase()}</Typography>
                            </Typography>
                        )}
                        {customerInfo?.phoneNumber && (
                            <Typography {...styles.info}>
                                {translate("Phone:")}
                                <Typography {...styles.content}>{formatNumber(customerInfo.phoneNumber)}</Typography>
                            </Typography>
                        )}
                        {customerInfo?.faxNumber && (
                            <Typography {...styles.info}>
                                {translate("Fax:")}
                                <Typography {...styles.content}>{formatNumber(customerInfo.faxNumber)}</Typography>
                            </Typography>
                        )}
                        {customerInfo?.email && (
                            <Link {...styles.emailIcon} href={`mailto:${customerInfo.email}`}>
                                <MailIcon />
                            </Link>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.invoiceContainer}>
                        <Typography {...styles.title}>{translate("Invoices Search")}</Typography>

                        <GridContainer {...styles.filtersContainer}>
                            <GridItem {...styles.poNumberGrid}>
                                <TextField
                                    label="PO Number, Invoice No., Reference No."
                                    placeholder={translate("Enter number here...")}
                                    type="text"
                                    value={poNumber}
                                    onChange={e => setPoNumber(e.target.value)}
                                    maxLength={25}
                                    {...styles.input}
                                    iconClickableProps={{ onClick: handleSearch }}
                                />
                            </GridItem>
                            <GridItem {...styles.clearBtnGridItem}>
                                <Button {...styles.clearBtn} onClick={clearSearch}>
                                    {translate("Clear")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceCustomerInfo),
    definition: {
        group: "Invoice History",
        displayName: "Invoice Customer Info",
    },
};

export default widgetModule;
